import { graphGetJson } from "../../email/services/graph";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";

/** Someone who can be invited. */
export interface Person {
  readonly name: string;
  /** Lower-cased. */
  readonly email: string;
}

/** How many matches the type-ahead asks Graph for: more than it lists, as people already invited are dropped. */
export const PEOPLE_SEARCH_TOP = 15;

interface UsersResponse {
  readonly value?: readonly {
    readonly displayName?: string | null;
    readonly mail?: string | null;
  }[];
}

interface DirectoryUsersResponse {
  readonly value?: readonly {
    readonly mail?: string | null;
    readonly jobTitle?: string | null;
    readonly department?: string | null;
  }[];
}

/** Graph accepts at most this many values in one `in (…)` filter. */
export const DESIGNATIONS_PER_REQUEST = 15;

/**
 * Colleagues' designations (job title, or department when there's no title),
 * keyed by lower-cased email; null where there's none. This needs a permission
 * that can read those fields, such as User.Read.All. With only
 * User.ReadBasic.All Graph doesn't return them, so nobody gets one. People
 * outside the organisation aren't in the directory and get none either.
 */
export async function fetchDesignations(
  token: string,
  refresh: RefreshTokenFn | undefined,
  emails: readonly string[],
): Promise<Map<string, string | null>> {
  const designations = new Map<string, string | null>(emails.map((e) => [e, null]));
  for (const u of await directoryEntries(token, refresh, emails)) {
    const email = (u.mail ?? "").trim().toLowerCase();
    const designation = (u.jobTitle ?? "").trim() || (u.department ?? "").trim();
    if (designations.has(email) && designation) designations.set(email, designation);
  }
  return designations;
}

/** The directory entries for `emails`, 15 addresses per request, one request after another. */
async function directoryEntries(
  token: string,
  refresh: RefreshTokenFn | undefined,
  emails: readonly string[],
): Promise<NonNullable<DirectoryUsersResponse["value"]>[number][]> {
  if (emails.length === 0) return [];
  // OData string literals escape a quote by doubling it.
  const quoted = emails
    .slice(0, DESIGNATIONS_PER_REQUEST)
    .map((e) => `'${e.replace(/'/g, "''")}'`)
    .join(",");
  const filter = `mail in (${quoted})`;
  // Graph's default page (100) holds every match for 15 addresses, even where two accounts share one.
  const result = await graphGetJson<DirectoryUsersResponse>(
    `/users?$filter=${encodeURIComponent(filter)}&$select=mail,jobTitle,department`,
    token,
    refresh,
  );
  const rest = await directoryEntries(token, refresh, emails.slice(DESIGNATIONS_PER_REQUEST));
  return [...(result.value ?? []), ...rest];
}

/**
 * A full email address, for adding someone the directory search can't find.
 * The domain's labels can't contain a dot, so each dot has one place to match
 * and a long input can't make the regex backtrack.
 */
export const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/;

/**
 * People in your organisation whose name or email address starts with
 * `query`, from the Graph directory (needs User.ReadBasic.All). Entries with
 * no mailbox are left out, since they can't be invited.
 */
export async function searchPeople(token: string, refresh: RefreshTokenFn | undefined, query: string): Promise<Person[]> {
  // Quotes and backslashes would break out of Graph's $search clause.
  const q = query.replace(/["\\]/g, " ").trim();
  if (!q) return [];
  const search = `"displayName:${q}" OR "mail:${q}"`;
  // Only accounts with a mailbox: a large directory has many without one (mail is null), and a
  // first page of those left nothing to offer. "ne null" is an advanced query, hence $count.
  const result = await graphGetJson<UsersResponse>(
    `/users?$search=${encodeURIComponent(search)}&$filter=${encodeURIComponent("mail ne null")}&$count=true` +
      `&$select=displayName,mail&$top=${PEOPLE_SEARCH_TOP}`,
    token,
    refresh,
    // Graph requires this header for $search, and for advanced queries, on directory objects.
    { ConsistencyLevel: "eventual" },
  );
  const people: Person[] = [];
  const seen = new Set<string>();
  for (const u of result.value ?? []) {
    const email = (u.mail ?? "").trim().toLowerCase();
    // Two directory accounts can share one address (e.g. someone's normal and admin accounts); offer it once.
    if (!email || seen.has(email)) continue;
    seen.add(email);
    people.push({ name: (u.displayName ?? "").trim() || email, email });
  }
  return people;
}
